import type { ReactNode } from 'react';
import { useState } from 'react';
import type { ArtifactRef, FormField } from '@visualitics-toolkit/shared';
import { SQL_INLINE_MAX_BYTES } from '@visualitics-toolkit/shared';

import { Checkbox, Field, FileDrop, Input, Select, Spinner, Textarea } from '../atoms';
import {
  BrandTypeahead,
  BusinessUnitSelect,
  CategoryTypeahead,
  OrgTypeahead,
  RetailerMultiselect,
  RetailerSelect,
  UserTypeahead,
} from '../molecules';
import { SqlLibrarySelect } from './SqlLibrarySelect';
import { api } from '../../lib/api-client';
import { gzipFileForUpload } from '../../lib/gzip-file';

export interface FormValues {
  [key: string]: unknown;
}

export interface ResolvedNames {
  organizationName: string | null;
  userEmail: string | null;
}

/**
 * Renderiza o formulário a partir das `fields` do registry — a UI não conhece
 * operação por operação, só o vocabulário de `FieldKind` (C6: catálogo declarativo).
 */
export function OperationForm({
  fields,
  values,
  errors,
  onChange,
  onResolvedNames,
}: {
  fields: readonly FormField[];
  values: FormValues;
  errors: Record<string, string>;
  onChange: (name: string, value: unknown) => void;
  onResolvedNames: (patch: Partial<ResolvedNames>) => void;
}): ReactNode {
  return (
    <div>
      {fields.map((field) => {
        const sibling = field.visibleWhen
          ? String(values[field.visibleWhen.field] ?? fieldDefault(fields, field.visibleWhen.field) ?? '')
          : '';
        if (field.visibleWhen && sibling !== field.visibleWhen.equals) return null;

        const value = values[field.name] ?? field.defaultValue;
        const disabled = Boolean(field.dependsOn && !values[field.dependsOn]);

        return (
          <Field
            key={field.name}
            label={field.required ? `${field.label} *` : field.label}
            help={field.help}
            error={errors[field.name]}
            htmlFor={`field-${field.name}`}
          >
            {renderControl(field, value, disabled, onChange, onResolvedNames, values, fields)}
          </Field>
        );
      })}
    </div>
  );
}

function renderControl(
  field: FormField,
  value: unknown,
  disabled: boolean,
  onChange: (name: string, value: unknown) => void,
  onResolvedNames: (patch: Partial<ResolvedNames>) => void,
  values: FormValues,
  fields: readonly FormField[],
): ReactNode {
  switch (field.kind) {
    case 'org-select':
      return (
        <OrgTypeahead
          value={asString(value)}
          onChange={(id) => onChange(field.name, id)}
          onResolved={
            field.name === 'organizationId'
              ? (organizationName) => onResolvedNames({ organizationName })
              : undefined
          }
        />
      );

    case 'brand-select':
      return (
        <BrandTypeahead
          organizationId={asString(values[field.dependsOn ?? 'organizationId'])}
          value={asString(value)}
          onChange={(brand) => onChange(field.name, brand)}
        />
      );

    case 'category-select':
      return (
        <CategoryTypeahead
          organizationId={asString(values[field.dependsOn ?? 'organizationId'])}
          value={asString(value)}
          onChange={(category) => onChange(field.name, category)}
        />
      );

    case 'bu-select':
      return (
        <BusinessUnitSelect
          organizationId={asString(values[field.dependsOn ?? 'organizationId'])}
          value={asString(value)}
          onChange={(businessUnitId) => onChange(field.name, businessUnitId)}
        />
      );

    case 'retailer-select':
      return (
        <RetailerSelect
          id={`field-${field.name}`}
          organizationId={asString(values[field.dependsOn ?? 'organizationId'])}
          value={asString(value)}
          onChange={(id) => onChange(field.name, id)}
        />
      );

    case 'retailer-multiselect':
      return (
        <RetailerMultiselect
          organizationId={asString(values[field.dependsOn ?? 'organizationId'])}
          value={Array.isArray(value) ? (value as string[]) : []}
          onChange={(siteConfigIds) => onChange(field.name, siteConfigIds)}
        />
      );

    case 'user-select':
      return (
        <UserTypeahead
          value={asString(value)}
          onChange={(id) => onChange(field.name, id)}
          onResolved={(userEmail) => onResolvedNames({ userEmail })}
        />
      );

    case 'sql-library-select':
      return (
        <SqlLibrarySelect value={asString(value)} onChange={(id) => onChange(field.name, id)} />
      );

    case 'file':
      return (
        <FileArtifactField field={field} value={value} disabled={disabled} onChange={onChange} />
      );

    case 'sql':
    case 'json':
    case 'textarea':
      return (
        <SqlOrJsonFileField
          field={field}
          value={value}
          disabled={disabled}
          onChange={onChange}
          canUploadArtifact={fields.some((entry) => entry.name === 'artifactId')}
        />
      );

    case 'boolean':
      return (
        <Checkbox
          label={field.help ?? 'Sim'}
          checked={Boolean(value)}
          disabled={disabled}
          onChange={(event) => onChange(field.name, event.target.checked)}
        />
      );

    case 'number':
      return (
        <Input
          id={`field-${field.name}`}
          type="number"
          value={value === undefined || value === null ? '' : String(value)}
          disabled={disabled}
          onChange={(event) =>
            onChange(field.name, event.target.value === '' ? undefined : Number(event.target.value))
          }
        />
      );

    case 'date':
      return (
        <Input
          id={`field-${field.name}`}
          type="date"
          value={asString(value)}
          disabled={disabled}
          onChange={(event) => onChange(field.name, event.target.value || undefined)}
        />
      );

    case 'select':
      return (
        <Select
          id={`field-${field.name}`}
          value={asString(value)}
          disabled={disabled}
          onChange={(event) => onChange(field.name, event.target.value || undefined)}
        >
          {!field.required && <option value="">Padrão</option>}
          {field.options?.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      );

    case 'multiselect':
      return (
        <div className="flex flex-wrap gap-3">
          {field.options?.map((option) => {
            const selected = Array.isArray(value) ? (value as string[]) : [];
            return (
              <Checkbox
                key={option.value}
                label={option.label}
                checked={selected.includes(option.value)}
                onChange={(event) =>
                  onChange(
                    field.name,
                    event.target.checked
                      ? [...selected, option.value]
                      : selected.filter((entry) => entry !== option.value),
                  )
                }
              />
            );
          })}
        </div>
      );

    default:
      return (
        <Input
          id={`field-${field.name}`}
          value={asString(value)}
          disabled={disabled}
          onChange={(event) => onChange(field.name, event.target.value || undefined)}
        />
      );
  }
}

function asString(value: unknown): string {
  return value === undefined || value === null ? '' : String(value);
}

function SqlOrJsonFileField({
  field,
  value,
  disabled,
  onChange,
  canUploadArtifact,
}: {
  field: FormField;
  value: unknown;
  disabled: boolean;
  onChange: (name: string, next: unknown) => void;
  canUploadArtifact: boolean;
}): ReactNode {
  const [busy, setBusy] = useState(false);
  const [filename, setFilename] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const isSql = field.kind === 'sql';

  async function uploadLargeSql(file: File): Promise<void> {
    setBusy(true);
    setError(null);
    setNote('Enviando o artefato…');
    try {
      const alreadyGzipped = file.name.toLowerCase().endsWith('.gz');
      const prepared =
        alreadyGzipped
          ? { file, originalBytes: file.size, uploadedBytes: file.size, gzipped: true }
          : file.size > 32 * 1024 * 1024
            ? { file, originalBytes: file.size, uploadedBytes: file.size, gzipped: false }
            : await gzipFileForUpload(file);
      const form = new FormData();
      form.append('file', prepared.file);
      const artifact = await api.postForm<ArtifactRef>('/artifacts/upload', form);
      setFilename(file.name);
      setNote(
        `${(prepared.originalBytes / 1024 / 1024).toFixed(1)} MiB → ${(prepared.uploadedBytes / 1024 / 1024).toFixed(1)} MiB${prepared.gzipped ? ' gzip' : ''}`,
      );
      onChange('sql', undefined);
      onChange('artifactId', artifact.id);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
      setNote(null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-2">
      <Textarea
        id={`field-${field.name}`}
        rows={field.kind === 'textarea' ? 4 : 10}
        value={asString(value)}
        disabled={disabled || busy}
        spellCheck={false}
        onChange={(event) => onChange(field.name, event.target.value)}
      />
      <FileDrop
        accept={field.kind === 'json' ? '.json,application/json' : '.sql,.sql.gz,.gz,text/plain'}
        label={
          busy
            ? 'Enviando artefato…'
            : `Carregar arquivo ${field.kind === 'json' ? '.json' : '.sql / .sql.gz'}`
        }
        maxInlineBytes={SQL_INLINE_MAX_BYTES}
        onLargeFile={isSql && canUploadArtifact ? (file) => void uploadLargeSql(file) : undefined}
        onFile={(content) => onChange(field.name, content)}
      />
      {busy ? <Spinner /> : null}
      {filename || note ? (
        <p className="font-mono text-xs text-muted">
          {filename}
          {note ? ` · ${note}` : ''}
        </p>
      ) : null}
      {error ? <p className="text-xs text-severity-irreversible">{error}</p> : null}
    </div>
  );
}

function FileArtifactField({
  field,
  value,
  disabled,
  onChange,
}: {
  field: FormField;
  value: unknown;
  disabled: boolean;
  onChange: (name: string, next: unknown) => void;
}): ReactNode {
  const [busy, setBusy] = useState(false);
  const [filename, setFilename] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function upload(file: File): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const artifact = await api.postForm<ArtifactRef>('/artifacts/upload', form);
      setFilename(file.name);
      onChange(field.name, artifact.id);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-2">
      <label className="flex cursor-pointer flex-col items-center justify-center gap-1 rounded-md border border-dashed border-border px-4 py-6 text-sm text-muted hover:border-accent">
        <span>{busy ? 'Enviando ZIP…' : 'Escolher arquivo .zip'}</span>
        {busy ? <Spinner /> : null}
        <input
          id={`field-${field.name}`}
          type="file"
          accept={field.accept ?? '.zip,application/zip'}
          className="sr-only"
          disabled={disabled || busy}
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) void upload(file);
          }}
        />
      </label>
      {asString(value) ? (
        <p className="font-mono text-xs text-muted">
          artifact {asString(value)}
          {filename ? ` · ${filename}` : ''}
        </p>
      ) : null}
      {error ? <p className="text-xs text-severity-irreversible">{error}</p> : null}
    </div>
  );
}

function fieldDefault(fields: readonly FormField[], name: string): string | boolean | undefined {
  return fields.find((field) => field.name === name)?.defaultValue;
}
